import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { handleLogin, handleVerifyCode, handleResendCode } from '../controllers/authController';
import { secureStorage } from '../utils/security';

const inputClass =
  'w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:outline-none focus:border-red-500 focus:bg-white transition';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step,    setStep]    = useState('login'); // 'login' | 'verify'
  const [form,    setForm]    = useState({ email: '', password: '' });
  const [userId,  setUserId]  = useState(null);
  const [code,    setCode]    = useState('');
  const [error,   setError]   = useState('');
  const [info,    setInfo]    = useState('');
  const [resent,  setResent]  = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const goToDashboard = (role) => {
    if (role === 'admin')         navigate('/dashboard/admin');
    else if (role === 'supplier') navigate('/dashboard/supplier');
    else                          navigate('/dashboard/customer');
  };

  const submit = async () => {
    setError(''); setInfo('');
    setLoading(true);
    const result = await handleLogin(form, login, navigate);
    setLoading(false);
    if (result?.needsVerification) {
      setUserId(result.user_id);
      setStep('verify');
      setInfo('E-posta adresinize gönderilen 6 haneli kodu girin.');
      return;
    }
    if (result?.error) setError(result.error);
  };

  const verifyCode = async () => {
    setError(''); setInfo('');
    setLoading(true);
    const result = await handleVerifyCode(userId, code);
    setLoading(false);
    if (result.error) { setError(result.error); return; }

    if (result.token && result.user) {
      secureStorage.setToken(result.token);
      login(result.user);
      goToDashboard(result.user.role);
    } else {
      setStep('login');
      setCode('');
      setInfo('E-posta adresiniz doğrulandı. Tedarikçi hesabınız admin onayı bekliyor.');
    }
  };

  const resend = async () => {
    setResent(false); setError('');
    const result = await handleResendCode(userId);
    if (result.error) setError(result.error);
    else setResent(true);
  };

  const onKey = (e) => { if (e.key === 'Enter') submit(); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-10 shadow-2xl">

        {/* Back */}
        <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-red-600 flex items-center gap-1 mb-6 transition">
          ← Ana Sayfa
        </button>

        {/* Logo */}
        <div className="text-2xl font-bold text-gray-900 mb-1">
          Oto<span className="text-red-700">ParçamıBul</span>
        </div>

        {/* Error / info / resent */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-5 mt-4">
            {error}
          </div>
        )}
        {info && !error && (
          <div className="bg-blue-50 border border-blue-100 text-blue-700 rounded-lg px-4 py-3 text-sm mb-5 mt-4">
            ℹ️ {info}
          </div>
        )}
        {resent && (
          <div className="bg-green-50 border border-green-100 text-green-700 rounded-lg px-4 py-3 text-sm mb-5">
            ✓ Yeni kod gönderildi.
          </div>
        )}

        {/* ── Login step ── */}
        {step === 'login' && (
          <>
            <h2 className="text-xl font-bold text-gray-800 mb-1">Tedarikçi Girişi</h2>
            <p className="text-gray-400 text-sm mb-8">OtoParçamıBul tedarikçi hesabınıza giriş yapın</p>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">E-posta</label>
              <input type="email" className={inputClass} placeholder="firma@email.com"
                value={form.email} maxLength={100}
                onChange={e => set('email', e.target.value)} onKeyDown={onKey} autoComplete="email" />
            </div>

            <div className="mb-6">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Şifre</label>
              <input type="password" className={inputClass} placeholder="••••••••"
                value={form.password} maxLength={128}
                onChange={e => set('password', e.target.value)} onKeyDown={onKey} autoComplete="current-password" />
            </div>

            <button onClick={submit} disabled={loading}
              className="w-full bg-red-700 hover:bg-red-800 disabled:bg-red-400 text-white font-semibold py-3 rounded-lg text-sm transition mb-5">
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>

            <div className="relative text-center text-gray-300 text-sm mb-5">
              <span className="bg-white px-3 relative z-10">veya</span>
              <div className="absolute inset-x-0 top-1/2 h-px bg-gray-200 z-0" />
            </div>

            <p className="text-center text-sm text-gray-500">
              Hesabınız yok mu?{' '}
              <button onClick={() => navigate('/register')} className="text-red-700 font-semibold hover:underline">
                Ücretsiz Kayıt Ol
              </button>
            </p>
          </>
        )}

        {/* ── Verify step ── */}
        {step === 'verify' && (
          <>
            <div className="text-center mb-6 mt-2">
              <div className="text-4xl mb-3">✉️</div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">E-posta Doğrulama</h2>
              <p className="text-gray-400 text-sm">
                {form.email} adresine gönderilen 6 haneli kodu girin.
              </p>
            </div>

            <input type="text" inputMode="numeric"
              className={`${inputClass} text-center text-2xl tracking-[0.5em] font-mono mb-4`}
              placeholder="000000" maxLength={6} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={e => e.key === 'Enter' && code.length === 6 && verifyCode()}
            />

            <button onClick={verifyCode} disabled={loading || code.length !== 6}
              className="w-full bg-red-700 hover:bg-red-800 disabled:bg-red-400 text-white font-semibold py-3 rounded-lg text-sm transition mb-3">
              {loading ? 'Doğrulanıyor...' : 'Doğrula & Giriş Yap'}
            </button>

            <div className="flex justify-between text-xs text-gray-400">
              <button onClick={() => { setStep('login'); setCode(''); setError(''); setInfo(''); }}
                className="hover:text-red-700 transition">← Geri</button>
              <button onClick={resend} className="hover:text-red-700 transition">Kod gelmedi? Tekrar gönder</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
